import React, { useState, useRef, useEffect, memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CardData, Action } from '../types';
import { Edit2, Trash2, X, Copy, Square, Link as LinkIcon, AlertTriangle, GitFork, Check, Clock, AlertCircle, Palette, Diamond, ClipboardList, Unlink, Maximize2, Minimize2, ArrowUp, ArrowRight, ArrowDown, ArrowLeft } from 'lucide-react';
import { ActivityNode } from './ActivityNode';
import { DecisionNode } from './DecisionNode';
import { InventoryTriangle } from './InventoryTriangle';
import { SubProcessCard } from './SubProcessCard';
import { VSM_LIBRARY } from './VSMLibrary';
import { useCardInteractions, notifyPostItJustCreated } from './hooks/useCardInteractions';
import { getPredictiveStatus, AlertStatus } from '../utils/calculations';
import { useProjectStore } from '../store/useProjectStore';
import { ActionSummaryModal } from './ActionSummaryModal';
import type { CardProps } from './Card/types';
import { getCardDimensions, getCardClassNames, getActionStatusColor } from './Card/cardUtils';
import { useTheme } from '../context/ThemeContext';
import { getAlertBorderClass, getAlertIcon, getAlertTooltip } from './Card/alertUtils';

const CardComponent: React.FC<CardProps> = ({
  data,
  selected,
  onSelect,
  onCardContextMenu,
  onEdit,
  onDeleteTotal,
  onMarkX,
  onDuplicate,
  onUngroup,
  onToggleExpand,
  isExpanded,
  onAddNext,
  onHoverAddPreview,
  onDragStart,
  onDrag,
  onDragEnd,
  scale,
  pan,
  isConnecting,
  onStartConnection,
  onFinishConnection,
  manualArrowMode,
  manualArrowSource,
  opacity,
  isBottleneck,
  isCreatingPostIt,
  postItCursor,
  onCreatePostItOnCard,
  onUpdateCardData,
  analysisMode,
  linkedPostIts,
  onPostItClick,
  onPostItStartDrag,
  onPostItContextMenu,
  onPostItTextChange,
  onOpenInMemo,
  onOpenImpactPanel,
  linkedActions: linkedActionsProp,
  isTimeScaleActive,
  dragEnabled,
  isKaizenMode,
  viewMode,
  heatmapMode,
  showPercentBadge,
  showAS,
  showFTE = true,
  liveMetrics,
  isSnapAligned = false,
  snapPulseNonce = 0,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false); // Estado para hover do card
  const [convertedHoverTarget, setConvertedHoverTarget] = useState<'stock' | 'supermarket' | null>(null);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Estado para controlar menu lateral
  const [isMenuAnimating, setIsMenuAnimating] = useState(false); // Estado para animação de entrada
  const [showActionSummary, setShowActionSummary] = useState(false); // Estado para controlar modal de resumo de ações
  const cardRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const snapPulseNonceRef = useRef(snapPulseNonce);
  const [snapPulsing, setSnapPulsing] = useState(false);

  useEffect(() => {
    if (!isSnapAligned || snapPulseNonce === snapPulseNonceRef.current) return;
    snapPulseNonceRef.current = snapPulseNonce;
    setSnapPulsing(true);
    const timer = window.setTimeout(() => setSnapPulsing(false), 100);
    return () => window.clearTimeout(timer);
  }, [isSnapAligned, snapPulseNonce]);
  
  // Usar hook de interações
  const {
    isDragging,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  } = useCardInteractions({
    data,
    isConnecting,
    isCreatingPostIt,
    onCreatePostItOnCard,
    onSelect,
    onDragStart,
    onDrag,
    onDragEnd,
    scale,
    pan,
    dragEnabled,
  });

  const isInitial = data.tipo === 'card_inicial';
  const isFinal = data.tipo === 'card_final';
  const isDecision = data.tipo === 'decisao';
  const isSubProcess = data.tipo === 'sub_processo';
  const isRemoved = data.status === 'removido';
  const isActionNode = data.tipo === 'acao';
    const isVsm = data.tipo?.startsWith('vsm_') || data.mapOrigin === 'FACTORY' || !!data.vsmIcon;
  const isVsmInventory = data.tipo === 'inventory' || ['estoque', 'pulmao', 'supermercado', 'fifo'].includes(data.vsmIcon || '');
  const isConvertedSupermarketInventory = isVsmInventory && (data as any).vsmConvertedToIcon === 'supermercado';
  const isPullBufferInventory = isVsmInventory && (
    ['supermercado', 'pulmao'].includes(data.vsmIcon ?? '') ||
    (data as any).vsmConvertedToIcon === 'supermercado'
  );
    const canConvertStockToSupermarket = data.tipo === 'inventory'
      && data.vsmIcon === 'estoque'
      && !!onUpdateCardData;

  // Efeito de exclusão: só aplica transparência no modo Kaizen
  let effectiveOpacity = isDragging ? 0.9 : (opacity ?? 1);
  if (isRemoved && isKaizenMode) {
    effectiveOpacity = 0.25;
  } else if (isRemoved) {
    effectiveOpacity = 1;
  }
  
  // Buscar ações vinculadas a este card
  const { getActionsByNode } = useProjectStore();
  const linkedActions = useMemo(() => {
    if (!data.id) return [];
    return getActionsByNode(data.id);
  }, [data.id, getActionsByNode]);

  // Obter primeira ação vinculada (para indicador e atalho)
  const primaryAction = linkedActions.length > 0 ? linkedActions[0] : null;
  
  // Bloquear seleção quando isKaizenMode for true e o card não for um ActionNode
  const canSelect = !isKaizenMode || isActionNode || isVsmInventory;

  // Limpar estado de hover e menu quando o card não está mais selecionado
  // Garante que o efeito visual de seleção seja removido instantaneamente
  useEffect(() => {
    if (!selected) {
      setIsHovered(false);
      setShowColorMenu(false);
      setIsMenuOpen(false);
      setIsMenuAnimating(false);
    }
  }, [selected]);

  // Controlar animação de entrada do menu
  useEffect(() => {
    if (isMenuOpen) {
      // Primeiro render com estilos iniciais (opacity: 0, translateX(6px))
      setIsMenuAnimating(true);
      // Após um frame, mudar para estilos finais (opacity: 1, translateX(0))
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsMenuAnimating(false);
        });
      });
      // Notificar outros cards para fechar seus menus
      window.dispatchEvent(new CustomEvent('card-menu-opened', { detail: { cardId: data.id } }));
    } else {
      setIsMenuAnimating(false);
    }
  }, [isMenuOpen, data.id]);

  // Escutar eventos de outros cards abrindo menu para fechar este
  useEffect(() => {
    const handleMenuOpened = (e: CustomEvent) => {
      if (e.detail.cardId !== data.id && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('card-menu-opened', handleMenuOpened as EventListener);
    return () => window.removeEventListener('card-menu-opened', handleMenuOpened as EventListener);
  }, [data.id, isMenuOpen]);

  // Fechar menu ao clicar fora (desktop)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        isMenuOpen &&
        cardRef.current &&
        menuRef.current &&
        !cardRef.current.contains(target) &&
        !menuRef.current.contains(target)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Wrappers para handlers com lógica de long press
  const handlePointerDownWrapper = (e: React.PointerEvent) => {
    // Cancelar timer se já existir
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    // Iniciar timer para long press (400-500ms) - apenas mobile e apenas para atividades
    if (e.pointerType === 'touch' && !isDecision && !isVsm) {
      longPressTimerRef.current = setTimeout(() => {
        if (!isConnecting && !isRemoved) {
          setIsMenuOpen(true);
          onSelect(data.id, e as any);
        }
        longPressTimerRef.current = null;
      }, 450);
    }

    // Chamar handler original do hook
    handlePointerDown(e);
  };

  const handlePointerUpWrapper = (e: React.PointerEvent) => {
    // Cancelar long press se pointer up
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    handlePointerUp(e);
  };

  const handlePointerLeaveWrapper = () => {
    // Cancelar long press se pointer leave
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  
  // Configuração Visual
  const { width: cardWidth, height: cardHeight, decisionSize } = getCardDimensions(
    data,
    isDecision,
    isTimeScaleActive
  );
  const updateConvertedHoverTarget = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isConvertedSupermarketInventory || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const containerW = isDecision ? 160 : ((isInitial || (isFinal && data.layoutType === 'classic')) ? 220 : cardWidth);
    const containerH = isDecision ? 160 : cardHeight;
    const scaleX = rect.width ? containerW / rect.width : 1;
    const scaleY = rect.height ? containerH / rect.height : 1;
    const localX = (event.clientX - rect.left) * scaleX;
    const localY = (event.clientY - rect.top) * scaleY;
    const offset = ((data as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
    const marketW = Math.round(cardWidth * 0.92);
    const marketH = Math.round(cardHeight * 0.98);
    const marketLeft = Math.round(cardWidth * 0.46) + (Number(offset.x) || 0);
    const marketTop = -Math.round(cardHeight * 1.08) + (Number(offset.y) || 0);
    const insideMarket = localX >= marketLeft && localX <= marketLeft + marketW && localY >= marketTop && localY <= marketTop + marketH;
    const insideStock = localX >= 0 && localX <= cardWidth && localY >= 0 && localY <= cardHeight;
    setConvertedHoverTarget(insideMarket ? 'supermarket' : insideStock ? 'stock' : null);
  };
  
  // Classes de Estilo
  const isClassicMode = data.layoutType === 'classic';
  const containerClass = getCardClassNames({
    isDecision,
    selected,
    isDragging,
    isHovered,
    isConnecting,
    isCreatingPostIt,
    dragEnabled,
    cardType: data.tipo,
  });
  
  // FASE 3: Calcular Alerta Preditivo
  const alertStatus: AlertStatus = getPredictiveStatus(data);
  
  // x HEATMAP: Calcular nível de Eficiência Lean (TRA / TP)
  const getHeatmapLevel = () => {
    if (!heatmapMode || isDecision || isInitial || isFinal) return null;
    
    const tra = data.tt || 0; // Tempo de Processamento (TRA - Touch Time)
    const tp = data.lt || data.tp || 0; // Tempo Total (TP - Lead Time)
    
    if (tp === 0 || tra === 0) return null;
    
    // Calcular % de Eficiência (TRA / TP * 100)
    // Quanto maior, melhor (menos desperdício/espera)
    const efficiency = (tra / tp) * 100;
    
    // LGICA LEAN CORRETA: Verde = Eficiente, Vermelho = Desperdício
    if (efficiency >= 90) return { level: 'excellent', color: 'bg-emerald-500', label: 'Excelente', value: efficiency }; // Verde
    if (efficiency >= 70) return { level: 'good', color: 'bg-green-500', label: 'Bom', value: efficiency }; // Verde claro
    if (efficiency >= 50) return { level: 'warning', color: 'bg-amber-500', label: 'Atenção', value: efficiency }; // Amarelo
    if (efficiency >= 30) return { level: 'poor', color: 'bg-orange-500', label: 'Ruim', value: efficiency }; // Laranja
    return { level: 'critical', color: 'bg-red-500', label: 'Crítico', value: efficiency }; // Vermelho
  };
  
  const heatmapLevel = getHeatmapLevel();
  
  // Cores Padrão - Todos os cards seguem a mesma formatação
  // Para modos default/postit/minimal, o ActivityNode já renderiza seu próprio container com fundo e bordas
  // Então não precisamos aplicar bg-white ou border aqui para evitar o retângulo cinza duplicado
  const { theme } = useTheme();
  const classicBg = theme === 'midnight' ? '#223244' : theme === 'black' ? '#131314' : 'white';
  let defaultBgClass = isClassicMode ? '' : 'bg-transparent';
  // Estado Base: borda transparente para modos não-clássicos (ActivityNode cuida das bordas)
  let borderColor = isBottleneck 
    ? 'border-red-500' // Gargalo mantém borda vermelha (apenas no modo clássico)
    : isDecision 
    ? 'border-transparent' // Decisões: sem borda visível no container (usa contorno interno)
    : isClassicMode
    ? 'border-slate-200' // Modo clássico: borda cinza clara sutil no estado base
    : 'border-transparent'; // Modos default/postit/minimal: transparente (ActivityNode cuida das bordas)
  
  const userBg = data.style?.backgroundColor;
  if (!userBg && !isDecision && !isBottleneck && isClassicMode) {
    // Apenas para modo clássico: borda padrão cinza sutil
    borderColor = 'border-slate-200';
  }
  
  // Aplicar classe de alerta preditivo (Fase 3) - DESATIVADO pois heatmap já mostra gargalos
  // const alertBorderClass = getAlertBorderClass(alertStatus, isDecision);
  const alertBorderClass = ''; // Removido para evitar borda vermelha extra quando heatmap está ativo
  
  // Lógica de Background - transparente para modos não-clássicos
  const bgStyle = (userBg && !isDecision && isClassicMode) ? { backgroundColor: userBg } : {};
  // Compatibilidade: verificar posicao ou position
  const pos = data.posicao || (data as any).position || { x: 0, y: 0 };

  return (
    <>
        <div
            data-card-id={data.id}
            className={`${containerClass} ${isDecision ? 'bg-transparent' : defaultBgClass} ${
              isDragging && !isClassicMode && !isDecision && !isInitial && !isFinal ? 'shadow-sm scale-[1.02]' : ''
            } ${isSnapAligned ? 'snap-align-highlight' : ''} ${snapPulsing ? 'snap-align-pulse' : ''} ${isCreatingPostIt ? 'hover:ring-2 hover:ring-blue-400 hover:ring-opacity-60' : ''} ${
              !isVsm && ((isFinal && !isInitial) || (isInitial && data.layoutType !== 'classic')) ? 'rounded-full overflow-hidden' : 'rounded'
            }`}
            style={{
              left: (isInitial && data.layoutType === 'classic') ? pos.x + 30 : pos.x, 
              top: isDecision ? pos.y + 10 : pos.y, // Centralizar decisão verticalmente (+10px para compensar diferença de 20px de altura)
              width: isDecision ? 160 : ((isInitial || (isFinal && data.layoutType === 'classic')) ? 220 : cardWidth), 
              // Permitir altura dinâmica para cards de ação ou layout post-it
              height: isDecision ? 160 : (data.tipo === 'acao' || data.layoutType === 'postit' ? 'auto' : cardHeight),
              minHeight: isDecision ? 160 : cardHeight,
              overflow: 'visible',
              opacity: effectiveOpacity,
              pointerEvents: 'auto', // Garantir que os cards sempre recebam eventos
              zIndex: isCreatingPostIt ? 1000 : data.vsmIcon === 'nivelamento' ? 200 : isPullBufferInventory ? 120 : undefined, // Nivelamento (OXOX) acima da camada kanban-loop (z=180); buffers puxados expõem handles acima das setas
              cursor: isCreatingPostIt ? (postItCursor || 'grabbing') : undefined, // Cursor personalizado com post-it colorido durante modo criação
              // CRÍTICO: Remover transição de posição (left/top) para manter sincronia com setas
              transition: isDragging ? 'none' : undefined,
              ...(isDecision ? { backgroundColor: 'transparent' } : bgStyle),
              ...(data.layoutType === 'classic' ? { 
                boxShadow: 'none',
                filter: 'none',
                WebkitFilter: 'none',
                // Ícones VSM com dimensões próprias (menores que 240px) — wrapper transparente
                backgroundColor: (isClassicMode && !!data.vsmIcon) ? 'transparent' : (isClassicMode ? classicBg : 'transparent'),
                borderRadius: 0,
                border: 'none'
              } : isDecision ? {
                boxShadow: 'none',
                filter: 'none',
                WebkitFilter: 'none'
              } : {})
            }}
            ref={cardRef}
            onMouseDown={(e) => {
              // Se estamos em modo de criação de Post-it, bloquear mouseDown (já tratado no pointerDown)
              if (isCreatingPostIt) {
                e.stopPropagation();
                return;
              }
            }}
            onPointerDown={handlePointerDownWrapper}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUpWrapper}
            onPointerLeave={handlePointerLeaveWrapper}
            onMouseMove={updateConvertedHoverTarget}
            onMouseEnter={() => {
              // Mostrar menu no hover (desktop) - apenas para atividades
              // Suprimir menu em modo criação de post-it (não interferir com a inserção)
              setIsHovered(true);
              if (!isConnecting && !isRemoved && !isDragging && !isDecision && !isCreatingPostIt && !isVsm) {
                setIsMenuOpen(true);
              }
            }}
            onMouseLeave={(e: React.MouseEvent) => {
              // CORRE!ÒO: Verificar se relatedTarget é um elemento válido antes de chamar closest
              const relatedTarget = e.relatedTarget;
              const isValidTarget = relatedTarget instanceof Element;

              const stayingInCardOrMenu = 
                (isValidTarget && relatedTarget.closest(`[data-card-id="${data.id}"]`)) || 
                (isValidTarget && relatedTarget.closest(`[data-card-menu="${data.id}"]`));

              if (!stayingInCardOrMenu && !selected) {
                setIsHovered(false);
                setIsMenuOpen(false);
              }
              setConvertedHoverTarget(null);
            }}
            onContextMenu={(e) => { 
                // Não processar menu de contexto quando estiver criando post-it
                if (isCreatingPostIt) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                if (onCardContextMenu?.(data.id, e)) {
                  return;
                }
                // Bloquear menu de contexto em Kaizen Mode (exceto ActionNode)
                if (!canSelect) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                e.preventDefault(); 
                e.stopPropagation(); 
                setMenuPos({ x: e.clientX, y: e.clientY }); 
                setShowMenu(true); 
                onSelect(data.id); 
            }}
            onDoubleClick={(e) => {
                // Não processar double click quando estiver criando post-it
                if (isCreatingPostIt) {
                  e.stopPropagation();
                  return;
                }
                // Bloquear double click em Kaizen Mode (exceto ActionNode)
                if (!canSelect) {
                  e.stopPropagation();
                  return;
                }
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setMenuPos({ x: rect.right + 10, y: rect.top });
                setShowMenu(true);
                onSelect(data.id);
            }}
            onClick={(e) => {
              // Se estamos em modo de criação de Post-it, bloquear click (já tratado no pointerDown)
              if (isCreatingPostIt) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }

              // Se estiver conectando (modo normal ou manual), finalizar conexão ao clicar no card
              if(isConnecting) { 
                e.stopPropagation(); 
                e.preventDefault();
                onFinishConnection(data.id); 
              } else if (!isDragging) {
                // Manter menu aberto ao clicar (desktop)
                if (isHovered && !isConnecting && !isRemoved && !isVsm) {
                  setIsMenuOpen(true);
                }
              }
            }}
        >
            {/* OVERLAY DE CAPTURA: cobre o card inteiro no modo criação de post-it.
                0 o último filho renderizado (z-index 9999) para interceptar qualquer clique antes dos
                filhos internos do card, garantindo 1 único clique para vincular o post-it. */}
            {isCreatingPostIt && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 9999,
                  borderRadius: 'inherit',
                  cursor: 'none',
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  notifyPostItJustCreated(); // seta flag antes de criar, garante que handlePointerUp nao chame onSelect
                  if (onCreatePostItOnCard) onCreatePostItOnCard(data.id, e.clientX, e.clientY);
                }}
                onPointerUp={(e) => {
                  // Impede que o pointerUp suba ao card e dispare onSelect
                  e.stopPropagation();
                  e.preventDefault();
                }}
              />
            )}
            {/* BADGES DE ANLISE (Gargalo, Retrabalho) - APENAS SE analysisMode ESTIVER ATIVO */}
            {analysisMode && !isDecision && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-50 flex gap-1" style={{ position: 'absolute' }}>
                    {isBottleneck && (
                        <div className="bg-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow flex items-center gap-1 animate-pulse">
                            <AlertTriangle size={10}/> GARGALO
                        </div>
                    )}
                    {(() => {
                        const hasLowQuality = (data.percentCompleteAccurate || 100) < 80;
                        return hasLowQuality && !isBottleneck && (
                            <div className="bg-orange-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-sm shadow flex items-center gap-1">
                                <AlertTriangle size={10}/> RETRABALHO
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* x BADGE DE HEATMAP - Agora renderizado no ActivityNode com lógica de prioridade */}

            {/* DESPERDÍCIOS */}
            {!isInitial && data.desperdicios && data.desperdicios.length > 0 && (
                <div 
                    className="absolute flex flex-col gap-1 z-40"
                    style={{ 
                        top: '-8px',
                        right: '-8px'
                    }}
                >
                    {data.desperdicios.slice(0, 2).map(w => (
                        <div key={w} className="w-5 h-5 bg-red-100 text-red-600 rounded-full border border-white shadow-sm flex items-center justify-center" title={w}><AlertTriangle size={10}/></div>
                    ))}
                    {data.desperdicios.length > 2 && <div className="w-5 h-5 bg-slate-100 text-slate-600 rounded-full border border-white shadow-sm flex items-center justify-center text-[9px] font-bold">+{data.desperdicios.length-2}</div>}
                </div>
            )}

            {/* --- MENU DE OP!"ES LATERAL (Direita) --- */}
            {!isConnecting && !isRemoved && !isDecision && !isVsm && isMenuOpen && (
              <div
                ref={menuRef}
                data-card-menu={data.id}
                className="absolute nodrag bg-enterprise-panel border border-enterprise-border-soft shadow-sm flex flex-col overflow-hidden"
                style={{
                  left: (isInitial && data.layoutType === 'classic') ? 'calc(100% - 16px)' : isInitial ? 'calc(100% - 4px)' : '100%',
                  top: 'calc(50% - 56px)', // Centraliza verticalmente (metade da altura do menu: 112px/2 = 56px)
                  height: '112px', // Dobro da altura anterior (56px * 2)
                  width: isInitial ? '50px' : '34px', // Mais largo para card inicial para cobrir o vão
                  borderWidth: '0.5px',
                  borderColor: '#e2e8f0', // slate-200 (padrão original)
                  borderTopRightRadius: '12px', // rounded do ActivityNode
                  borderBottomRightRadius: '12px', // rounded do ActivityNode
                  borderTopLeftRadius: isInitial ? '12px' : 0, // Arredondamento no canto esquerdo apenas para card inicial
                  borderBottomLeftRadius: isInitial ? '12px' : 0, // Arredondamento no canto esquerdo apenas para card inicial
                  borderLeft: isInitial ? '0.5px solid #e2e8f0' : 'none',
                  opacity: isMenuAnimating ? 0 : 1,
                  transform: isMenuAnimating ? 'translateX(6px)' : 'translateX(0)',
                  transition: 'opacity 150ms ease-out, transform 150ms ease-out',
                  pointerEvents: 'auto',
                  zIndex: -1,
                  paddingLeft: isInitial ? '16px' : '0', // Padding para deslocar os ícones para a direita no card inicial
                }}
                onMouseEnter={() => {
                  // Manter menu aberto ao hover
                  setIsMenuOpen(true);
                }}
                onMouseLeave={(e) => {
                  // Fechar se não estiver mais sobre card ou menu
                  // Verificar se é uma instância de Element antes de chamar closest
                  const relatedTarget = e.relatedTarget;
                  const stayingInCard = relatedTarget instanceof Element && relatedTarget.closest(`[data-card-id="${data.id}"]`);
                  if (!stayingInCard && !selected) {
                    setIsMenuOpen(false);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Adicionar Etapa do Processo - 1/3 da altura */}
                <button
                  className="w-full h-full flex-1 hover:bg-blue-100 hover:shadow-inner flex items-center justify-center text-sm transition-all duration-200 group rounded-none"
                  style={{ height: '28px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onHoverAddPreview?.(null, null);
                    onAddNext(data.id, 'atividade', true);
                  }}
                  onMouseEnter={() => onHoverAddPreview?.(data.id, 'atividade', true)}
                  onMouseLeave={() => onHoverAddPreview?.(null, null)}
                  title="Adicionar Etapa do Processo"
                >
                  <Square size={14} className="text-blue-500 group-hover:scale-110 transition-transform" fill="currentColor" />
                </button>

                {/* Adicionar Decisão - 1/3 da altura */}
                <button
                  className="w-full h-full flex-1 hover:bg-green-100 hover:shadow-inner flex items-center justify-center text-sm transition-all duration-200 group rounded-none"
                  style={{ height: '28px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onHoverAddPreview?.(null, null);
                    onAddNext(data.id, 'decisao', true);
                  }}
                  onMouseEnter={() => onHoverAddPreview?.(data.id, 'decisao', true)}
                  onMouseLeave={() => onHoverAddPreview?.(null, null)}
                  title="Adicionar Decisão"
                >
                  <Diamond size={14} className="text-green-500 group-hover:scale-110 transition-transform" />
                </button>
                
                {/* Conectar manualmente - 1/3 da altura */}
                <button
                  className="w-full h-full flex-1 hover:bg-slate-100 hover:shadow-inner flex items-center justify-center text-sm transition-all duration-200 group rounded-none"
                  style={{ height: '28px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onStartConnection(data.id, 'right');
                  }}
                  title="Conectar manualmente"
                >
                  <LinkIcon size={14} className="text-slate-500 group-hover:scale-110 transition-transform" />
                </button>
              </div>
            )}

            {/* --- BOTÒO EXCLUIR (Canto superior direito) - Aparece apenas quando selected --- */}
            {/* No modo Kaizen, mostra mesmo em cards removidos para permitir exclusão definitiva */}
            {!isConnecting && (!isRemoved || viewMode === 'kaizen_work') && (
              <button
                className={`absolute nodrag z-50
                  top-2 right-2
                  w-7 h-7 rounded-md flex items-center justify-center
                  transition-all shadow-sm opacity-0 hover:opacity-100
                  backdrop-blur-sm ${isRemoved ? 'bg-red-100/90 hover:bg-red-200 text-red-600 hover:text-red-800' : 'bg-white/80 hover:bg-red-50 text-slate-400 hover:text-red-600'}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const label = data.atividade || data.nome_fluxo || 'esta etapa';
                  const msg = isRemoved
                    ? `Excluir definitivamente "${label}"? Esta ação não pode ser desfeita.`
                    : `Excluir a etapa "${label}"?`;
                  if (!window.confirm(msg)) return;
                  try {
                    await onDeleteTotal(data.id);
                  } catch (error) {
                    console.error('[Card] Erro ao excluir card:', error);
                  }
                }}
                title={isRemoved ? "Excluir definitivamente" : "Excluir etapa"}
              >
                <Trash2 size={14} />
              </button>
            )}

            {/* Feedback visual para modo manual: esperando seleção de fonte */}
            {manualArrowMode && !manualArrowSource && (
              <div
                className="absolute bg-green-50/30 flex items-center justify-center z-[70] rounded border-2 border-green-400 border-dashed cursor-crosshair"
                style={{ inset: -10 }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onStartConnection(data.id, 'right');
                }}
              >
                <div className="bg-green-500 text-white text-xs px-2 py-1 rounded shadow-lg animate-pulse">
                  Clique para iniciar
                </div>
              </div>
            )}

            {manualArrowMode && manualArrowSource && manualArrowSource.id !== data.id && (
              <div
                className="absolute bg-cyan-50/20 flex items-center justify-center z-[70] rounded border-2 border-cyan-400 border-dashed cursor-alias"
                style={{ inset: -10 }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onFinishConnection(data.id);
                }}
              >
                <div className="bg-cyan-500 text-white text-xs px-2 py-1 rounded shadow-lg">
                  Clique para conectar
                </div>
              </div>
            )}
            
            {/* Feedback visual para modo manual: este é o card fonte */}
            {manualArrowMode && manualArrowSource?.id === data.id && (
              <div className="absolute inset-0 bg-blue-50/30 flex items-center justify-center z-40 rounded border-2 border-blue-500 cursor-crosshair">
                <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded shadow-lg">
                  x Origem
                </div>
              </div>
            )}

            {/* RENDERIZA!ÒO DO CARD - Usando componentes especializados */}
            {data.tipo === 'inventory' ? (
              <InventoryTriangle
                data={data}
                selected={selected}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                onUpdateCardData={onUpdateCardData}
                handlePointerDown={handlePointerDown}
                handlePointerMove={handlePointerMove}
                handlePointerUp={handlePointerUp}
              />
            ) : isDecision ? (
              <DecisionNode
                data={data}
                selected={selected}
                isDragging={isDragging}
                isConnecting={isConnecting}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                decisionSize={decisionSize}
                handlePointerDown={handlePointerDown}
                handlePointerMove={handlePointerMove}
                handlePointerUp={handlePointerUp}
                onFinishConnection={onFinishConnection}
                onAddNext={onAddNext}
                onStartConnection={onStartConnection}
                onSelect={onSelect}
              />
            ) : isSubProcess ? (
              <SubProcessCard
                data={data}
                selected={selected}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                onDoubleClick={() => {
                  // Drill-down: navegar para o fluxo vinculado
                  // Será implementado via contexto/hook
                }}
                onEdit={onEdit}
                onUngroup={onUngroup ? () => onUngroup(data.id) : undefined}
                onToggleExpand={onToggleExpand}
                isExpanded={isExpanded}
                scale={scale}
                opacity={opacity}
                isBottleneck={isBottleneck}
              />
            ) : (
              <ActivityNode
                data={data}
                selected={selected}
                isDragging={isDragging}
                isConnecting={isConnecting}
                isBottleneck={isBottleneck}
                analysisMode={analysisMode}
                opacity={opacity}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                onEdit={onEdit}
                handlePointerDown={handlePointerDown}
                handlePointerMove={handlePointerMove}
                handlePointerUp={handlePointerUp}
                onPostItStartDrag={onPostItStartDrag}
                scale={scale}
                onFinishConnection={onFinishConnection}
                linkedPostIts={linkedPostIts}
                onPostItClick={onPostItClick}
                onPostItContextMenu={onPostItContextMenu}
                onPostItTextChange={onPostItTextChange}
                onOpenInMemo={onOpenInMemo}
                linkedActions={linkedActions}
                onShowActionSummary={(cardId) => {
                  if (cardId === data.id) {
                    setShowActionSummary(true);
                  }
                }}
                onUpdateCardData={onUpdateCardData}
                onOpenImpactPanel={onOpenImpactPanel}
                viewMode={viewMode}
                heatmapMode={heatmapMode}
                isKaizenMode={isKaizenMode}
                showPercentBadge={showPercentBadge}
                showAS={showAS}
                showFTE={showFTE}
                liveMetrics={liveMetrics}
              />
            )}

            {/* VISUAL TIMELINE: Barra de Progresso quando Time Scale está ativo */}
            {isTimeScaleActive && !isDecision && !isInitial && !isFinal && (
              <div 
                className="absolute bottom-0 left-0 w-full h-1.5 bg-gray-100 rounded-b-md overflow-hidden flex"
                style={{
                  zIndex: 5, // Acima do conteúdo do card
                }}
              >
                {/* Parte Cheia (Representa o TP - Tempo de Processamento) */}
                <div 
                  className="h-full bg-blue-500 opacity-50" 
                  style={{ width: '100%' }}
                ></div>
                
                {/* Label flutuante centralizado */}
                <div className="absolute w-full text-center" style={{ top: '-15px' }}>
                  <span 
                    className="text-[9px] font-bold text-blue-600 bg-white px-1 rounded shadow-sm border border-blue-100"
                    style={{
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    {data.lt || data.tt || 0} min
                  </span>
                </div>
              </div>
            )}

            {/* HANDLES DE CONEXÃO — 4 pontos de ancoragem para cards VSM/Factory */}
            {isVsm && !isRemoved && (() => {
              const isPullBufferNode = isPullBufferInventory;
              const visible = isConnecting || isHovered || selected;
              const handleSize = 14;
              const handleHalf = handleSize / 2;
              const handleClass = "group flex h-[14px] w-[14px] items-center justify-center rounded-full cursor-crosshair transition-all hover:scale-125";
              const dotClass = "flex h-2.5 w-2.5 items-center justify-center rounded-full bg-cyan-400 text-white border border-white shadow-sm group-hover:h-3 group-hover:w-3 group-hover:bg-cyan-500 group-hover:shadow-md transition-all";
              const base: React.CSSProperties = { pointerEvents: visible ? 'auto' : 'none', position: 'absolute', opacity: visible ? 1 : 0, transition: 'opacity 0.2s', zIndex: isPullBufferNode ? 2000 : 80 };
              // Container dimensions (mirrors inline style logic at lines 315-318)
              const cW = isDecision ? 160 : ((isInitial || (isFinal && data.layoutType === 'classic')) ? 220 : cardWidth);
              const cH = isDecision ? 160 : cardHeight;
              // Offset do ícone visual dentro do container (centralizado)
              const vL = (cW - cardWidth) / 2;
              const vT = (cH - cardHeight) / 2;
              // Estoque (triângulo VSM): handles nas arestas do triângulo, não no bbox retangular
              const isTriangle = data.vsmIcon === 'estoque';
              const isSmallKanbanCard = ['kanban_prod', 'kanban_ret'].includes(data.vsmIcon ?? '');
              const isKanbanLoteCard = data.vsmIcon === 'kanban_lote';
              const smallKanbanVisualH = isSmallKanbanCard ? cardHeight * 0.60 : cardHeight;
              const smallKanbanVisualTop = isSmallKanbanCard ? cardHeight * 0.20 : 0;
              const smallKanbanTopY = vT + smallKanbanVisualTop;
              const smallKanbanMidY = vT + smallKanbanVisualTop + smallKanbanVisualH / 2;
              const smallKanbanBottomY = vT + smallKanbanVisualTop + smallKanbanVisualH;
              const smallKanbanRightX = vL + cardWidth;
              const smallKanbanLeftX = vL;
              const kanbanLoteVisualTop = cardHeight * 0.04;
              const kanbanLoteVisualH = cardHeight * 0.70;
              const kanbanLoteRightX = vL + cardWidth;
              const kanbanLoteTopY = vT + kanbanLoteVisualTop;
              const kanbanLoteMidY = vT + kanbanLoteVisualTop + kanbanLoteVisualH / 2;
              const kanbanLoteBottomY = vT + kanbanLoteVisualTop + kanbanLoteVisualH;
              const kanbanLoteLeftX = vL;
              // Entidade externa (fornecedor/cliente/externo): cardHeight inclui a data box abaixo
              // do edifício. O edifício SVG tem sempre 90px. Handles L/R ficam centrados no edifício,
              // hBottom fica na base do edifício (90px), não no final do card.
              const isEntity = ['fornecedor', 'cliente', 'externo'].includes(data.vsmIcon ?? '');
              const entityShapeH = isEntity ? 90 : cardHeight;
              // Entidade externa: os "dentes" do edifício ocupam ~30px (topo).
              // Centro visual da parede plana: (30 + 90) / 2 = 60px
              const entityMidY   = isEntity ? 60 : cardHeight / 2;
              // Posição de cada handle: centro do dot (4px) na borda do shape visual
              const convertedMarketOffset = ((data as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
              const convertedMarketW = Math.round(cardWidth * 0.92);
              const convertedMarketH = Math.round(cardHeight * 0.98);
              const convertedMarketLeft = Math.round(cardWidth * 0.46) + (Number(convertedMarketOffset.x) || 0);
              const convertedMarketTop = -Math.round(cardHeight * 1.08) + (Number(convertedMarketOffset.y) || 0);
              const useConvertedMarketHandles = data.vsmIcon === 'estoque' && (data as any).vsmConvertedToIcon === 'supermercado';
              const showAllConvertedHandles = selected || isConnecting;
              const stockHandlesVisible = useConvertedMarketHandles ? showAllConvertedHandles || convertedHoverTarget === 'stock' : visible;
              const marketHandlesVisible = useConvertedMarketHandles ? showAllConvertedHandles || convertedHoverTarget === 'supermarket' : visible;
              const stockBase: React.CSSProperties = { ...base, opacity: stockHandlesVisible ? 1 : 0, pointerEvents: stockHandlesVisible ? 'auto' : 'none' };
              const marketBase: React.CSSProperties = { ...base, opacity: marketHandlesVisible ? 1 : 0, pointerEvents: marketHandlesVisible ? 'auto' : 'none' };
              const isSupermarket = data.vsmIcon === 'supermercado';
              const marketOutlineInsetX = isSupermarket || useConvertedMarketHandles ? Math.max(2, Math.round((isSupermarket ? cardWidth : convertedMarketW) * 0.10)) : 0;
              const marketOutlineInsetY = isSupermarket || useConvertedMarketHandles ? Math.max(1, Math.round((isSupermarket ? cardHeight : convertedMarketH) * 0.02)) : 0;
              const hTop: React.CSSProperties    = isTriangle
                ? { ...stockBase, left: vL + cardWidth * 0.50 - handleHalf, top: vT + cardHeight * 0.04 - handleHalf }
                : isSmallKanbanCard
                ? { ...base, left: vL + cardWidth / 2 - handleHalf, top: smallKanbanTopY - handleSize }
                : isKanbanLoteCard
                ? { ...base, left: vL + cardWidth / 2 - handleHalf, top: kanbanLoteTopY - handleSize }
                : isSupermarket
                ? { ...base, left: vL + cardWidth / 2 - handleHalf, top: vT + marketOutlineInsetY - handleHalf }
                : { ...base, left: vL + cardWidth / 2 - handleHalf, top: vT - handleHalf };
              const hRight: React.CSSProperties  = isTriangle
                ? { ...stockBase, left: vL + cardWidth * 0.75 - handleHalf, top: vT + cardHeight * 0.50 - handleHalf }
                : isSmallKanbanCard
                ? { ...base, left: smallKanbanRightX, top: smallKanbanMidY - handleHalf }
                : isKanbanLoteCard
                ? { ...base, left: kanbanLoteRightX, top: kanbanLoteMidY - handleHalf }
                : isSupermarket
                ? { ...base, left: vL + cardWidth - marketOutlineInsetX - handleHalf, top: vT + cardHeight / 2 - handleHalf }
                : { ...base, left: vL + cardWidth - handleHalf, top: vT + entityMidY - handleHalf };
              const hBottom: React.CSSProperties = isTriangle
                ? { ...stockBase, left: vL + cardWidth * 0.50 - handleHalf, top: vT + cardHeight * 0.92 - handleHalf }
                : isSmallKanbanCard
                ? { ...base, left: vL + cardWidth / 2 - handleHalf, top: smallKanbanBottomY }
                : isKanbanLoteCard
                ? { ...base, left: vL + cardWidth / 2 - handleHalf, top: kanbanLoteBottomY }
                : isSupermarket
                ? { ...base, left: vL + cardWidth / 2 - handleHalf, top: vT + cardHeight - marketOutlineInsetY - handleHalf }
                : { ...base, left: vL + cardWidth / 2 - handleHalf, top: vT + entityShapeH - handleHalf };
              const hLeft: React.CSSProperties   = isTriangle
                ? { ...stockBase, left: vL + cardWidth * 0.25 - handleHalf, top: vT + cardHeight * 0.50 - handleHalf }
                : isSmallKanbanCard
                ? { ...base, left: smallKanbanLeftX - handleSize, top: smallKanbanMidY - handleHalf }
                : isKanbanLoteCard
                ? { ...base, left: kanbanLoteLeftX - handleSize, top: kanbanLoteMidY - handleHalf }
                : isSupermarket
                ? { ...base, left: vL + marketOutlineInsetX - handleHalf, top: vT + cardHeight / 2 - handleHalf }
                : { ...base, left: vL - handleHalf, top: vT + entityMidY - handleHalf };
              const marketTopHandle: React.CSSProperties = { ...marketBase, left: vL + convertedMarketLeft + convertedMarketW / 2 - handleHalf, top: vT + convertedMarketTop + marketOutlineInsetY - handleHalf };
              const marketRightHandle: React.CSSProperties = { ...marketBase, left: vL + convertedMarketLeft + convertedMarketW - marketOutlineInsetX - handleHalf, top: vT + convertedMarketTop + convertedMarketH / 2 - handleHalf };
              const marketBottomHandle: React.CSSProperties = { ...marketBase, left: vL + convertedMarketLeft + convertedMarketW / 2 - handleHalf, top: vT + convertedMarketTop + convertedMarketH - marketOutlineInsetY - handleHalf };
              const marketLeftHandle: React.CSSProperties = { ...marketBase, left: vL + convertedMarketLeft + marketOutlineInsetX - handleHalf, top: vT + convertedMarketTop + convertedMarketH / 2 - handleHalf };
              const renderHandle = (style: React.CSSProperties, title: string, handle: 'top' | 'right' | 'bottom' | 'left', icon: React.ReactNode) => (
                <div className={`nodrag ${handleClass}`} style={style} title={title}
                  onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onStartConnection(data.id, handle); }}>
                  <div className={dotClass}>{icon}</div>
                </div>
              );
              return (
                <>
                  {renderHandle(hTop, 'Criar seta para cima', 'top', <ArrowUp size={8} strokeWidth={3} />)}
                  {renderHandle(hRight, 'Criar seta para a direita', 'right', <ArrowRight size={8} strokeWidth={3} />)}
                  {renderHandle(hBottom, 'Criar seta para baixo', 'bottom', <ArrowDown size={8} strokeWidth={3} />)}
                  {renderHandle(hLeft, 'Criar seta para a esquerda', 'left', <ArrowLeft size={8} strokeWidth={3} />)}
                  {useConvertedMarketHandles && (
                    <>
                      {renderHandle(marketTopHandle, 'Criar seta para cima no supermercado', 'top', <ArrowUp size={8} strokeWidth={3} />)}
                      {renderHandle(marketRightHandle, 'Criar seta para a direita no supermercado', 'right', <ArrowRight size={8} strokeWidth={3} />)}
                      {renderHandle(marketBottomHandle, 'Criar seta para baixo no supermercado', 'bottom', <ArrowDown size={8} strokeWidth={3} />)}
                      {renderHandle(marketLeftHandle, 'Criar seta para a esquerda no supermercado', 'left', <ArrowLeft size={8} strokeWidth={3} />)}
                    </>
                  )}
                </>
              );
            })()}
        </div>
        
        {/* Modal de Resumo de Ações */}
        <ActionSummaryModal
          isOpen={showActionSummary}
          onClose={() => setShowActionSummary(false)}
          actions={linkedActions}
          cardTitle={data.atividade || data.nome_fluxo || 'Card'}
          onOpenInMemo={onOpenInMemo || (() => {})}
          cardId={data.id}
        />
        
        {showMenu && createPortal(
            <>
                <div className="fixed inset-0 z-[9998]" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} onContextMenu={(e) => { e.preventDefault(); setShowMenu(false); }}/>
                <div className="fixed z-[9999] bg-enterprise-panel shadow-sm rounded-md border border-enterprise-border-soft py-1 min-w-[160px]" style={{ left: menuPos.x, top: menuPos.y }}>
                    <button onClick={() => { onEdit(data.id); setShowMenu(false); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700"><Edit2 size={14}/> Editar</button>
                    <button onClick={() => { onDuplicate(data.id); setShowMenu(false); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700"><Copy size={14}/> Duplicar</button>
                    {/* Opção de Desagrupar para Sub-Processos */}
                    {isSubProcess && onUngroup && (
                      <>
                        <div className="h-px bg-slate-100 my-1"></div>
                        <button 
                          onClick={() => { 
                            onUngroup(data.id); 
                            setShowMenu(false); 
                          }} 
                          className="w-full text-left px-4 py-2 hover:bg-amber-50 flex items-center gap-2 text-sm text-amber-600 font-medium"
                          title="Restaurar os cards originais que foram agrupados"
                        >
                          <Unlink size={14}/> Desagrupar
                        </button>
                      </>
                    )}
                    {canConvertStockToSupermarket && (
                      <>
                        <div className="h-px bg-slate-100 my-1"></div>
                        <button
                          onClick={() => {
                            onUpdateCardData?.(data.id, {
                              vsmIcon: 'estoque',
                              atividade: data.atividade ?? 'Estoque',
                              inventoryMode: 'coverage',
                              vsmBufferDays: Number((data as any).vsmBufferDays ?? (data as any).vsmDias ?? 1) || 1,
                              vsmConvertedFromIcon: 'estoque',
                              vsmConvertedToIcon: 'supermercado',
                              vsmOriginalInventory: Number((data as any).inventory) || 0,
                              vsmOriginalInventoryUnit: (data as any).vsmUnit || 'LE',
                              vsmSupermarketOverlayOffset: (data as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 },
                            } as any);
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center gap-2 text-sm text-orange-600 font-medium"
                          title="Indicar supermercado acima do estoque preservando o estoque e as setas"
                        >
                          <Square size={14}/> {isConvertedSupermarketInventory ? 'Reaplicar Supermercado' : 'Converter em Supermercado'}
                        </button>
                      </>
                    )}
                    {onOpenInMemo && !isInitial && !isFinal && (
                      <>
                        <div className="h-px bg-slate-100 my-1"></div>
                        <button 
                          onClick={() => { 
                            onOpenInMemo(data.id); 
                            setShowMenu(false); 
                          }} 
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center gap-2 text-sm text-blue-600 font-medium"
                          title="Transforme este ponto do processo em uma ação executável."
                        >
                          <ClipboardList size={14}/> Criar Ação de Melhoria
                        </button>
                      </>
                    )}
                    <div className="h-px bg-slate-100 my-1"></div>
                    {!isInitial && !isFinal && (
                        <>
                            <button onClick={() => { onMarkX(data.id); setShowMenu(false); }} className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center gap-2 text-sm ${isRemoved ? 'text-orange-600 font-semibold' : 'text-slate-700'}`}>
                                <X size={14}/> {isRemoved ? 'Restaurar' : 'Marcar com X'}
                            </button>
                            {onUpdateCardData && (
                                <>
                                    <button onClick={() => { onUpdateCardData(data.id, { percentCompleteAccurate: 50 }); setShowMenu(false); }} className="w-full text-left px-4 py-2 hover:bg-red-50 flex items-center gap-2 text-sm text-red-600">
                                        <AlertTriangle size={14}/> Registrar Problema
                                    </button>
                                    <button onClick={() => { onUpdateCardData(data.id, { tipo_atividade: 'NVA' }); setShowMenu(false); }} className="w-full text-left px-4 py-2 hover:bg-yellow-50 flex items-center gap-2 text-sm text-yellow-600">
                                        <Clock size={14}/> Marcar Desperdício
                                    </button>
                                </>
                            )}
                        </>
                    )}
                    <button onClick={async (e) => { 
                      e.stopPropagation();
                      e.preventDefault();
                      setShowMenu(false);
                      const label = data.atividade || data.nome_fluxo || 'esta etapa';
                      if (!window.confirm(`Excluir a etapa "${label}"? Esta ação não pode ser desfeita.`)) return;
                      try {
                        await onDeleteTotal(data.id);
                      } catch (error) {
                        console.error('[Card] Erro ao excluir card:', error);
                      }
                    }} className="w-full text-left px-4 py-2 hover:bg-red-50 flex items-center gap-2 text-sm text-red-600"><Trash2 size={14}/> Excluir</button>
                </div>
            </>, document.body
        )}
    </>
  );
};

// Memoizar o componente Card para evitar re-renders desnecessários
// Isso é crítico para performance com muitos cards (50+)
const Card = memo(CardComponent, (prevProps, nextProps) => {
  // COMPARAÇÃO otimizada: evitar JSON.stringify pesado
  
  const prevPos = prevProps.data.posicao || { x: 0, y: 0 };
  const nextPos = nextProps.data.posicao || { x: 0, y: 0 };

  // Se for uma movimentação (pan/zoom), verificar se mudou o suficiente para rerenderizar
  // Ou se for zoom, podemos ser mais permissivos? 
  // Na verdade, o ideal é que scale/pan não disparem rerenders pesados.

  return (
    prevProps.data.id === nextProps.data.id &&
    prevPos.x === nextPos.x &&
    prevPos.y === nextPos.y &&
    prevProps.selected === nextProps.selected &&
    prevProps.opacity === nextProps.opacity &&
    prevProps.isBottleneck === nextProps.isBottleneck &&
    prevProps.scale === nextProps.scale &&
    prevProps.isConnecting === nextProps.isConnecting &&
    prevProps.manualArrowMode === nextProps.manualArrowMode &&
    prevProps.manualArrowSource?.id === nextProps.manualArrowSource?.id &&
    prevProps.manualArrowSource?.handle === nextProps.manualArrowSource?.handle &&
    // COMPARAÇÃO rasa de objetos complexos
    prevProps.data.resources === nextProps.data.resources &&
    prevProps.data.desperdicios === nextProps.data.desperdicios &&
    prevProps.linkedPostIts?.length === nextProps.linkedPostIts?.length &&
    // Campos de texto básicos
    prevProps.data.atividade === nextProps.data.atividade &&
    prevProps.data.tt === nextProps.data.tt &&
    prevProps.data.lt === nextProps.data.lt &&
    prevProps.data.layoutType === nextProps.data.layoutType &&
    prevProps.data.layoutColor === nextProps.data.layoutColor &&
    prevProps.isCreatingPostIt === nextProps.isCreatingPostIt &&
    prevProps.canEdit === nextProps.canEdit &&
    prevProps.dragEnabled === nextProps.dragEnabled &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.data.status === nextProps.data.status &&
    prevProps.data.sector === nextProps.data.sector &&
    (prevProps.data as any).setor === (nextProps.data as any).setor &&
    // Campos adicionais que afetam o visual do card no canvas
    prevProps.data.nome_fluxo === nextProps.data.nome_fluxo &&
    (prevProps.data as any).tipo_atividade === (nextProps.data as any).tipo_atividade &&
    (prevProps.data as any).percentLivreErros === (nextProps.data as any).percentLivreErros &&
    (prevProps.data as any).percentCompleteAccurate === (nextProps.data as any).percentCompleteAccurate &&
    prevProps.data.entrada === nextProps.data.entrada &&
    (prevProps.data as any).sistema === (nextProps.data as any).sistema &&
    (prevProps.data as any).carga === (nextProps.data as any).carga &&
    (prevProps.data as any).como === (nextProps.data as any).como &&
    (prevProps.data as any).unidadeDemanda === (nextProps.data as any).unidadeDemanda &&
    (prevProps.data as any).vsmIcon === (nextProps.data as any).vsmIcon &&
    (prevProps.data as any).vsmNome === (nextProps.data as any).vsmNome &&
    (prevProps.data as any).vsmProduto === (nextProps.data as any).vsmProduto &&
    (prevProps.data as any).vsmFrequencia === (nextProps.data as any).vsmFrequencia &&
    (prevProps.data as any).vsmDemanda === (nextProps.data as any).vsmDemanda &&
    (prevProps.data as any).vsmLinha4 === (nextProps.data as any).vsmLinha4 &&
    (prevProps.data as any).vsmLinha5 === (nextProps.data as any).vsmLinha5 &&
    (prevProps.data as any).decisao_sim_pct === (nextProps.data as any).decisao_sim_pct &&
    (prevProps.data as any).decisao_nao_pct === (nextProps.data as any).decisao_nao_pct &&
    (prevProps.data as any).decisao_sim_label === (nextProps.data as any).decisao_sim_label &&
    (prevProps.data as any).decisao_nao_label === (nextProps.data as any).decisao_nao_label
  );
}) as React.FC<CardProps>;

export { Card }; 
export default Card;


